import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import {
    Button,
    Spinner,
    Container,
} from "react-bootstrap";
import {
    FaArrowRight
} from "react-icons/fa";
import Layout from "../../hocs/Layout";
import { get_datos_oferta_erp, sync_renovacion_progress, relanzar_oferta, cancelar_oferta, mostrar_precios_oferta } from "../../services/ofertas";
import { determineContentTypeAndExtension } from "../../utils/ofertasUtils";
import { includesBvLabel } from "../../utils/bateriaVirtual";

import { notifyError } from "../../utils/notifications";

import GestionArchivosOferta from "../../components/modals/GestionArchivosOferta";
import NotasOferta from "../../components/modals/NotasOferta";
import SendSignaturitModal from "../../components/modals/SendSignaturitModal";
import RelanzarOfertaModal from "../../components/modals/RelanzarOfertaModal";
import ConfirmRelanzarModal from "../../components/modals/ConfirmRelanzarModal";
import OfferHeader from "../../components/ofertas/OfferHeader";
import OfferProgressTimeline from "../../components/ofertas/OfferProgressTimeline";
import CancelOfertaModal from "../../components/modals/CancelOfertaModal";

// Data Cards
import EconomicDataCard from "../../components/cards/EconomicDataCard";
import ClientCard from "../../components/cards/ClientCard";
import PayerCard from "../../components/cards/PayerCard";
import CupsCard from "../../components/cards/CupsCard";
import VirtualBatteryCard from "../../components/cards/VirtualBatteryCard";

import { aceptar_oferta } from "../../services/ofertas";
import { print_contrato_pdf } from "../../services/auxiliares";
import { send_document_signaturit } from "../../services/signaturit";
import { getOfferSignaturitEmail } from "../../utils/signaturit";
import "./OfertaDetail.css";

const extractSubtypeText = (value) => {
    if (Array.isArray(value)) {
        if (value.length > 1) return String(value[1] || value[0] || "");
        return String(value[0] || "");
    }
    if (value && typeof value === "object") {
        return String(value.name || value.display_name || value.label || value.value || value.id || "");
    }
    return String(value || "");
};

const normalizeSubtype = (value) =>
    extractSubtypeText(value)
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");

const normalizeStageState = (value) =>
    String(value || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");

const isOfferEditLockedByState = (stateName) => {
    const normalized = normalizeStageState(stateName);
    if (!normalized) return false;

    return [
        "aceptada",
        "oferta_aceptada",
        "firmado",
        "firmada",
        "signed",
        "won",
        "ganado",
        "pendiente_solicitud_atr",
        "rechazada_sol_atr",
        "solicitud_atr",
        "aceptada_sol_atr",
        "aceptada_solicitud_atr",
        "contrato_activado",
        "oferta_no_aceptada",
        "oferta_cancelada",
    ].includes(normalized);
};

const RELANZAR_SUBTYPE_MATRIX_ELECTRICO = {
    alta_nuevo: [
        "alta_nuevo",
        "cambio_comercializador",
        "cambio_comerc_titular",
        "cambio_comercializador_potencia",
        "cambio_comerc_titular_potencia",
        "cambio_comercializador_autoconsumo",
        "cambio_comerc_titular_autoconsumo",
        "cambio_comerc_potencia_autoconsumo",
        "cambio_completo",
    ],
    cambio_comercializador: ["alta_nuevo", "cambio_comercializador", "cambio_comerc_titular"],
    cambio_comerc_titular: ["alta_nuevo", "cambio_comercializador", "cambio_comerc_titular"],
    cambio_comercializador_potencia: ["alta_nuevo", "cambio_comercializador_potencia", "cambio_comerc_titular_potencia"],
    cambio_comercializador_autoconsumo: ["alta_nuevo", "cambio_comercializador_autoconsumo", "cambio_comerc_titular_autoconsumo"],
    cambio_comerc_potencia_autoconsumo: ["alta_nuevo", "cambio_comerc_potencia_autoconsumo", "cambio_completo"],
    cambio_comerc_titular_potencia: ["alta_nuevo", "cambio_comercializador_potencia", "cambio_comerc_titular_potencia"],
    cambio_comerc_titular_autoconsumo: ["alta_nuevo", "cambio_comercializador_autoconsumo", "cambio_comerc_titular_autoconsumo"],
    cambio_completo: ["alta_nuevo", "cambio_comerc_potencia_autoconsumo", "cambio_completo"],
};

const RELANZAR_SUBTYPE_GAS_OPTIONS = ["alta_nuevo", "cambio_comercializador", "cambio_comerc_titular"];
const RELANZAR_SUBTYPE_GAS_SUPPORTED = new Set(RELANZAR_SUBTYPE_GAS_OPTIONS);

const getAllowedRelaunchSubtypes = (currentSubtype, sectorType) => {
    if (String(sectorType || "").toLowerCase() === "gas") {
        return RELANZAR_SUBTYPE_GAS_SUPPORTED.has(currentSubtype) ? RELANZAR_SUBTYPE_GAS_OPTIONS : [];
    }
    return RELANZAR_SUBTYPE_MATRIX_ELECTRICO[currentSubtype] || [];
};

const canonicalSubtype = (value) => {
    let normalized = normalizeSubtype(value);
    if (normalized.endsWith("_gas")) {
        normalized = normalized.slice(0, -4);
    }
    const aliases = {
        alta_nueva: "alta_nuevo",
        cambio_de_comercializador: "cambio_comercializador",
        cambio_de_comercializador_y_titular: "cambio_comerc_titular",
        cambio_de_comercializador_y_potencia: "cambio_comercializador_potencia",
        cambio_comerc_potencia: "cambio_comercializador_potencia",
        cambio_de_comercializador_y_autoconsumo: "cambio_comercializador_autoconsumo",
        cambio_comerc_autoconsumo: "cambio_comercializador_autoconsumo",
        cambio_de_comercializador_y_potencia_y_autoconsumo: "cambio_comerc_potencia_autoconsumo",
        cambio_comercializador_potencia_autoconsumo: "cambio_comerc_potencia_autoconsumo",
        cambio_de_comercializador_y_titular_y_potencia: "cambio_comerc_titular_potencia",
        cambio_de_comercializador_y_titular_y_autoconsumo: "cambio_comerc_titular_autoconsumo",
        cambio_de_comercializador_y_titular_y_potencia_y_autoconsumo: "cambio_completo",
    };
    return aliases[normalized] || normalized;
};

const OfertaDetail = ({ user }) => {
    const { oferta_id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [oferta, setOferta] = useState(null);
    const [loading, setLoading] = useState(true);

    // Modal states

    const [showGestionArchivos, setShowGestionArchivos] = useState(false);
    const [showNotas, setShowNotas] = useState(false);
    const [showSignaturit, setShowSignaturit] = useState(false);

    // Action specific states
    const [ofertaActiva, setOfertaActiva] = useState(null); // Para gestion archivos
    const [ofertaNotas, setOfertaNotas] = useState(null);
    const [ofertaSignaturit, setOfertaSignaturit] = useState(null);
    const [showRelanzarModal, setShowRelanzarModal] = useState(false);
    const [ofertaToRelaunch, setOfertaToRelaunch] = useState(null);
    const [showConfirmRelanzar, setShowConfirmRelanzar] = useState(false);

    // Wizard state
    const [currentStep, setCurrentStep] = useState('cliente');

    useEffect(() => {
        const fetchOferta = async () => {
            setLoading(true);
            try {
                const data = await get_datos_oferta_erp(oferta_id);
                setOferta(data);
            } catch (error) {
                console.error("Error loading offer:", error);
            } finally {
                setLoading(false);
            }
        };

        if (oferta_id) {
            fetchOferta();
        }
    }, [oferta_id]);

    // Calculate virtual battery status
    const llistaPreuGlobal =
        oferta?.llista_preu_global ||
        oferta?.crm_polissa?.llista_preu_global ||
        oferta?.crm_polissa?.llista_preu;
    // const autoconsumoValue = oferta?.autoconsumo;
    // const hasAutoconsumo =
    //     autoconsumoValue !== null &&
    //     autoconsumoValue !== undefined &&
    //     String(autoconsumoValue).trim() !== "" &&
    //     String(autoconsumoValue) !== "00";
    // const showVirtualBattery =
    //     (hasAutoconsumo && includesBvLabel(llistaPreuGlobal)) ||
    //     (oferta?.bateria_virtual && Object.keys(oferta.bateria_virtual).length > 0);

    const autoconsumoValue = oferta?.autoconsumo;
    const hasAutoconsumo = autoconsumoValue === "41" || autoconsumoValue === "42" || autoconsumoValue === "43";
    const showVirtualBattery = (hasAutoconsumo && includesBvLabel(llistaPreuGlobal)) && !!oferta?.bateria_virtual;

    const normalizedSubType = normalizeSubtype(oferta?.sub_type_offer);
    const normalizedType = normalizeSubtype(oferta?.type || oferta?.type_offer || oferta?.tipo_oferta);
    const isRenewal =
        normalizedSubType.startsWith("renovacion")
        || normalizedSubType.includes("renovacion")
        || normalizedType.startsWith("renovacion");
    const isStateLocked = isOfferEditLockedByState(oferta?.state);
    const blockedStageIds = isStateLocked
        ? ["tarifa", "cliente", "pagador", "cups", "bateria_virtual", "documentacion"]
        : (isRenewal ? ["cliente", "pagador"] : []);
    const blockedStageRoutes = isRenewal ? ["partner", "pagador"] : [];



    const refreshOferta = async () => {
        try {
            const data = await get_datos_oferta_erp(oferta_id);
            setOferta(data);
        } catch (error) {
            console.error("Error refreshing offer:", error);
        }
    };

    // --- Action Handlers ---
    const aceptarOferta = async (id, sector_type) => {
        await aceptar_oferta(id, user.id, sector_type);
        refreshOferta();
    };

    const contratoPdf = async (user_id, id, polissa_id) => {
        try {
            const preciosResult = await mostrar_precios_oferta(id);
            if (!preciosResult) {
                return;
            }

            const archivo = await print_contrato_pdf(user_id, id, polissa_id);
            Object.keys(archivo).forEach((arch) => {
                const { datas: base64Data, name: originalName } = archivo[arch];
                const { contentType, extension } = determineContentTypeAndExtension(base64Data);

                const byteCharacters = atob(base64Data);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);

                const blob = new Blob([byteArray], { type: contentType });
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);

                link.download = originalName || `archivo.${extension}`;
                link.click();
                URL.revokeObjectURL(link.href);
            });
        } catch (error) {
            console.error("Error al descargar el archivo:", error);
        }
    };

    const openGestionArchivos = (id, name) => {
        setOfertaActiva({ id, name });
        setShowGestionArchivos(true);
    };

    const openNotas = (id, name) => {
        setOfertaNotas({ id, name });
        setShowNotas(true);
    };

    const openSignaturit = (oferta) => {
        if (!oferta.cliente_vat) {
            notifyError("Falta el CIF/NIF del cliente. Es obligatorio para enviar a Signaturit.");
            return;
        }
        setOfertaSignaturit(oferta);
        setShowSignaturit(true);
    };

    const onConfirmSignaturit = async (email, signatureType) => {
        if (!ofertaSignaturit?.id) return;
        try {
            await send_document_signaturit(ofertaSignaturit.id, signatureType, email);
        } catch (err) {
            console.error("Error al enviar a Signaturit:", err);
        } finally {
            refreshOferta();
        }
    };

    const handleAutoValidate = async (id) => {
        const updatedOferta = await sync_renovacion_progress(id);
        if (updatedOferta) {
            setOferta(updatedOferta);
        }
    };

    const handleRelanzarOferta = (currentOferta) => {
        const current = canonicalSubtype(currentOferta?.sub_type_offer);
        const allowed = getAllowedRelaunchSubtypes(current, currentOferta?.sector_type);
        if (!allowed.length) {
            notifyError("No hay subtipos configurados para relanzar esta oferta.");
            return;
        }
        setOfertaToRelaunch(currentOferta);
        setShowConfirmRelanzar(true);
    };

    const handleConfirmRelanzarChangar = () => {
        setShowConfirmRelanzar(false);
        setShowRelanzarModal(true);
    };

    const handleConfirmRelanzarMantener = async () => {
        if (!ofertaToRelaunch) return;
        try {
            const currentSubtype = canonicalSubtype(ofertaToRelaunch?.sub_type_offer);
            const updatedOferta = await relanzar_oferta(ofertaToRelaunch.id, currentSubtype);
            if (updatedOferta) {
                setOferta(updatedOferta);
            }
        } finally {
            setOfertaToRelaunch(null);
        }
    };

    const handleConfirmRelaunch = async (selectedSubtype) => {
        if (!ofertaToRelaunch) return;
        try {
            const updatedOferta = await relanzar_oferta(ofertaToRelaunch.id, selectedSubtype);
            if (updatedOferta) {
                setOferta(updatedOferta);
            }
        } finally {
            setOfertaToRelaunch(null);
        }
    };

//CANCELAR OFERTA
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [selectedOferta, setSelectedOferta] = useState(null);
    // Handler to open the cancel modal
    const handleCancelarClick = (oferta) => {
        setSelectedOferta(oferta);
        setShowCancelModal(true);
    };
    // Handler to confirm cancellation
    const handleCancelarOferta = async (oferta) => {
        const updatedOferta = await cancelar_oferta(oferta.id);
        if (updatedOferta) {
            setOferta(updatedOferta);
        }
    };

    const handleTimelineNavigation = (route) => {
        if (isStateLocked) {
            return;
        }

        if (blockedStageRoutes.includes(route)) {
            return;
        }

        if (route === 'resumen') {
            // Already here or navigate to self (refresh?)
            navigate(`/contratos/ofertas/${oferta_id}`);
        } else {
            navigate(`/${oferta.sector_type}/${route}/${oferta_id}`);
        }
    };

    if (loading) {
        return (
            <Layout lg={10} md={12}>
                <div className="text-center mt-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3">Cargando oferta...</p>
                </div>
            </Layout>
        );
    }

    if (!oferta) {
        return (
            <Layout title="Error">
                <div className="p-5 text-center">
                    <h3>Oferta no encontrada</h3>
                </div>
            </Layout>
        );
    }

    return (
        <Layout
            lg={12}
            md={12}
            user={user}
            offerContext={{ sector_type: oferta.sector_type, oferta_id: oferta.id }}
        >
            <div className="py-5 bg-light min-vh-100">
                <Container>
                    {/* ... (Header Card) ... */}
                    <OfferHeader
                        oferta={oferta}
                        user={user}
                        onNavigateBack={() => navigate(`/contratos/ofertas${location.search}`)}
                        onAceptar={aceptarOferta}
                        onImprimirContrato={contratoPdf}
                        onGestionArchivos={openGestionArchivos}
                        onVerNotas={openNotas}
                        onEnviarSignaturit={openSignaturit}
                        onSyncRenovacion={handleAutoValidate}
                        onRelanzar={handleRelanzarOferta}
                        onCancelar={handleCancelarClick}
                    />

                    {/* Timeline de Progreso - Horizontal arriba */}
                    {oferta.progress_data && (
                        <div className="mb-4">
                            <OfferProgressTimeline
                                progressData={oferta.progress_data}
                                ofertaId={oferta.id}
                                sectorType={oferta.sector_type}
                                horizontal={true}
                                currentStep={currentStep}
                                onStepClick={setCurrentStep}
                                onNavigate={handleTimelineNavigation}
                                blockedStages={blockedStageIds}
                                // Manté la línia de progrés sincronitzada amb condicions d’activació de BV.
                                showVirtualBattery={showVirtualBattery}
                            />
                        </div>
                    )}

                    {/* Wizard - Pantallas de edición */}
                    {/* Dashboard Content - Data Cards */}
                    <div className="row g-4 animation-fade-in">
                        {/* 1. Tariff & Economic Data */}
                        <div className="col-12 col-xl-6">
                            <EconomicDataCard
                                economicData={oferta}
                                onEdit={() => handleTimelineNavigation('tarifas')}
                            />
                        </div>

                        {/* 2. Client Data */}
                        <div className="col-12 col-xl-6">
                            <ClientCard
                                cliente={{
                                    nombre: [oferta.cliente_nombre, oferta.cliente_apellidos].filter(Boolean).join(' '),
                                    vat: oferta.cliente_vat,
                                    email: oferta.cliente_email,
                                    phone: oferta.cliente_phone,
                                    mobile: oferta.cliente_mobile
                                }}
                                onEdit={isRenewal ? undefined : () => handleTimelineNavigation('partner')}
                            />
                        </div>

                        {/* 3. Payer Data (Conditional) */}
                        {oferta.pagador_nombre && (
                            <div className="col-12 col-xl-6">
                                <PayerCard
                                    pagador={{
                                        name: oferta.pagador_nombre,
                                        vat: oferta.pagador_vat,
                                        // Fallback to client data if same entity (same VAT) and missing info
                                        email: oferta.pagador_email ||
                                            (oferta.pagador_vat === oferta.cliente_vat ? oferta.cliente_email : ''),
                                        phone: oferta.pagador_phone ||
                                            (oferta.pagador_vat === oferta.cliente_vat ? (oferta.cliente_phone || oferta.cliente_mobile) : '')
                                    }}
                                    onEdit={isRenewal ? undefined : () => handleTimelineNavigation('pagador')}
                                />
                            </div>
                        )}

                        {/* 4. CUPS Data */}
                        <div className="col-12 col-xl-6">
                            <CupsCard
                                cupsData={{
                                    code: oferta.cups_name,
                                    address: oferta.cups_street,
                                    city: oferta.cups_city,
                                    province: oferta.cups_provincia,
                                    zip: oferta.cups_zip,
                                    tarifa: oferta.tarifa,
                                    consumo: oferta.consumo_anual_previsto,
                                    cnae: oferta.cnae || oferta.cnae_gas
                                }}
                                onEdit={() => handleTimelineNavigation('cups')}
                            />
                        </div>

                        {/* 5. Virtual Battery (Conditional) */}
                        {showVirtualBattery && (
                            <div className="col-12">
                                <VirtualBatteryCard
                                    bateriaVirtual={oferta.bateria_virtual}
                                    onEdit={() => handleTimelineNavigation('bateria-virtual')}
                                />
                            </div>
                        )}
                    </div>

                    {/* Navigation Buttons (Wizard Style) */}
                    <div className="d-flex justify-content-end mt-4">
                        <Button
                            variant="primary"
                            size="lg"
                            className="rounded-pill px-4"
                            onClick={() => handleTimelineNavigation('tarifas')}
                            disabled={isStateLocked}
                        >
                            Siguiente Paso <FaArrowRight className="ms-2" />
                        </Button>
                    </div>

                </Container>
            </div>


            <GestionArchivosOferta
                show={showGestionArchivos}
                close={setShowGestionArchivos}
                oferta={ofertaActiva}
                onClose={refreshOferta}
            />
            <NotasOferta
                show={showNotas}
                close={setShowNotas}
                oferta={ofertaNotas}
                onClose={refreshOferta}
                user={user}
            />
            <SendSignaturitModal
                show={showSignaturit}
                onHide={() => setShowSignaturit(false)}
                defaultEmail={getOfferSignaturitEmail(ofertaSignaturit)}
                onClose={refreshOferta}
                onConfirm={onConfirmSignaturit}
                signatureType="email"
            />

            <ConfirmRelanzarModal
                show={showConfirmRelanzar}
                onHide={() => setShowConfirmRelanzar(false)}
                currentSubtype={ofertaToRelaunch ? canonicalSubtype(ofertaToRelaunch?.sub_type_offer) : ""}
                onConfirmMantener={handleConfirmRelanzarMantener}
                onConfirmChangar={handleConfirmRelanzarChangar}
            />

            <RelanzarOfertaModal
                show={showRelanzarModal}
                onHide={() => setShowRelanzarModal(false)}
                allowedSubtypes={ofertaToRelaunch ? getAllowedRelaunchSubtypes(canonicalSubtype(ofertaToRelaunch?.sub_type_offer), ofertaToRelaunch?.sector_type) : []}
                onConfirm={handleConfirmRelaunch}
            />

            <CancelOfertaModal
                show={showCancelModal}
                onHide={() => setShowCancelModal(false)}
                onConfirm={handleCancelarOferta}
                oferta={selectedOferta}
            />
        </Layout>
    );
};

const mapStateToProps = (state) => ({
    user: state.Auth.user,
    isAuthenticated: state.Auth.isAuthenticated,
});

export default connect(mapStateToProps)(OfertaDetail);
